import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { MessageModal } from './MessageModal';
interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ChangePasswordModal({
  isOpen,
  onClose
}: ChangePasswordModalProps) {
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setModalContent({
        title: 'Password Mismatch',
        message: 'New password and confirmation do not match.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    if (formData.newPassword.length < 8) {
      setModalContent({
        title: 'Weak Password',
        message: 'Password must be at least 8 characters long.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    setModalContent({
      title: 'Password Changed',
      message: 'Your password has been changed successfully. Please sign in again with your new password.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    setTimeout(() => {
      onClose();
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }, 2000);
  };
  return <>
      <div data-testid="change-password-modal-overlay" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-451">
        <div data-testid="change-password-modal" className="bg-white border border-gray-300 max-w-md w-full shadow-lg" data-id="element-452">
          <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-453">
            <h3 data-testid="change-password-modal-title" className="font-bold text-lg" data-id="element-454">
              Change Password
            </h3>
            <button data-testid="change-password-modal-close-btn" onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-455">
              <X className="w-5 h-5" data-id="element-456" />
            </button>
          </div>

          <form data-testid="change-password-form" onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-457">
            {/* Current Password */}
            <div data-id="element-458">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-459">
                Current Password
              </label>
              <div className="relative" data-id="element-460">
                <input data-testid="change-password-current-input" type={showPasswords.current ? 'text' : 'password'} required value={formData.currentPassword} onChange={e => setFormData({
                ...formData,
                currentPassword: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-461" />
                <button data-testid="change-password-current-toggle-btn" type="button" onClick={() => setShowPasswords({
                ...showPasswords,
                current: !showPasswords.current
              })} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-462">
                  {showPasswords.current ? <EyeOff className="w-4 h-4" data-id="element-463" /> : <Eye className="w-4 h-4" data-id="element-464" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div data-id="element-465">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-466">
                New Password
              </label>
              <div className="relative" data-id="element-467">
                <input data-testid="change-password-new-input" type={showPasswords.new ? 'text' : 'password'} required value={formData.newPassword} onChange={e => setFormData({
                ...formData,
                newPassword: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-468" />
                <button data-testid="change-password-new-toggle-btn" type="button" onClick={() => setShowPasswords({
                ...showPasswords,
                new: !showPasswords.new
              })} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-469">
                  {showPasswords.new ? <EyeOff className="w-4 h-4" data-id="element-470" /> : <Eye className="w-4 h-4" data-id="element-471" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-472">Minimum 8 characters</p>
            </div>

            {/* Confirm Password */}
            <div data-id="element-473">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-474">
                Confirm New Password
              </label>
              <div className="relative" data-id="element-475">
                <input data-testid="change-password-confirm-input" type={showPasswords.confirm ? 'text' : 'password'} required value={formData.confirmPassword} onChange={e => setFormData({
                ...formData,
                confirmPassword: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-476" />
                <button data-testid="change-password-confirm-toggle-btn" type="button" onClick={() => setShowPasswords({
                ...showPasswords,
                confirm: !showPasswords.confirm
              })} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-477">
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" data-id="element-478" /> : <Eye className="w-4 h-4" data-id="element-479" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4" data-id="element-480">
              <button data-testid="change-password-cancel-btn" type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 text-sm font-bold hover:bg-gray-50" data-id="element-481">
                Cancel
              </button>
              <button data-testid="change-password-submit-btn" type="submit" className="px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-482">
                Change Password
              </button>
            </div>
          </form>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-483" />
    </>;
}